/* eslint-disable eqeqeq */
import axios, { AxiosResponse } from "axios";
import Cookies from "js-cookie";
import { QueryClient } from "react-query";
import { notification } from "antd";
import i18n from "../i18n";

export const PATH = window.location.host === "test-klient.proste.menu"
  ? "https://test-server.proste.menu"
  : (process.env.REACT_APP_API_URL || "http://localhost:8000");

export const axiosInstance = axios.create({
  withCredentials: true,
});

// Flag to prevent multiple simultaneous logout notifications
let isLoggingOut = false;

const autoLogout = () => {
  // Prevent multiple simultaneous logout attempts — once triggered, never reset
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  Cookies.remove("accessToken");
  Cookies.remove("refreshToken");
  Cookies.remove("role");

  notification.info({
    message: i18n.t("client.sessionExpired"),
    description: i18n.t("client.sessionExpiredDescription"),
    placement: "bottomRight",
    duration: 3,
  });

  setTimeout(() => {
    window.location.reload();
  }, 1500);
};

axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = Cookies.get("accessToken");
    config.headers["Authorization"] = `Bearer ${accessToken}`;

    // IMPERSONATION: Add X-Impersonate-Company header if developer is impersonating
    const role = Cookies.get("role");
    const impersonatedCompanyId = Cookies.get("impersonatedCompanyId");

    if (role === "DEVELOPER" && impersonatedCompanyId) {
      config.headers["X-Impersonate-Company"] = impersonatedCompanyId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh queue for axiosInstance
let isRefreshingMain = false;
let failedQueueMain: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueueMain = (error: any = null) => {
  failedQueueMain.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueueMain = [];
};

// Global response interceptor with token refresh support
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401) {
      // Skip refresh for login check endpoint
      const isLoginCheck = originalRequest?.url?.includes('/is-logged-in');
      if (isLoginCheck) {
        Cookies.remove("accessToken");
        Cookies.remove("role");
        return Promise.reject(error);
      }

      // Skip refresh for login endpoints
      const isLoginEndpoint = originalRequest?.url?.includes('/login');
      if (isLoginEndpoint) {
        return Promise.reject(error);
      }

      // Try to refresh the token
      if (!originalRequest._retry) {
        if (isRefreshingMain) {
          return new Promise((resolve, reject) => {
            failedQueueMain.push({ resolve, reject });
          })
            .then(() => axiosInstance(originalRequest))
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshingMain = true;

        const refreshToken = Cookies.get("refreshToken");

        if (!refreshToken) {
          autoLogout();
          return Promise.reject(error);
        }

        try {
          const response = await axios.post(
            `${PATH}/v1/common_api/auth/refresh-token`,
            { refreshToken }
          );

          const { accessToken } = response.data;
          Cookies.set("accessToken", accessToken, { expires: 7, path: '/' });

          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          processQueueMain();

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueueMain(refreshError);
          autoLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshingMain = false;
        }
      }

      // If retry already happened and still 401, logout
      autoLogout();
    }

    return Promise.reject(error);
  }
);

export const login = async (data: {
  username: any;
  password: any;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/login`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const logout = async (): Promise<any> => {
  return await axiosInstance.post<any>(`${PATH}/logout`);
};

export const isLoggedIn = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/is-logged-in`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      Cookies.remove("accessToken");
      Cookies.remove("role");
      return null;
    }
    throw error;
  }
};

export const getServicesCategories = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/admin/service_categories`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getCompanyBranches = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/company_branches`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const uploadBranchLogo = async (branchId: number, file: Blob): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file, 'logo.jpg');
  const response: AxiosResponse<any> = await axiosInstance.post<any>(
    `${PATH}/v1/common_api/admin/company_branch/${branchId}/logo`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const uploadBranchBanner = async (branchId: number, file: Blob): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file, 'banner.jpg');
  const response: AxiosResponse<any> = await axiosInstance.post<any>(
    `${PATH}/v1/common_api/admin/company_branch/${branchId}/banner`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const editCompanyBranch = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/common_api/admin/company_branch/${data.id}`,
      data.data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const uploadProductImage = async (productId: number, file: Blob): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file, 'product.jpg');
  const response: AxiosResponse<any> = await axiosInstance.post<any>(
    `${PATH}/v1/products_api/admin/product/${productId}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const generateClaimLink = async (branchId: number): Promise<{ claimToken: string }> => {
  const response: AxiosResponse<any> = await axiosInstance.post(
    `${PATH}/v1/common_api/admin/company_branch/${branchId}/generate-claim`
  );
  return response.data;
};

export interface TestPrinterResult {
  ok: boolean;
  message?: string;
  error?: string;
  printerId?: string;
}

export const testPrinter = async (branchId: number): Promise<TestPrinterResult> => {
  const response: AxiosResponse<TestPrinterResult> = await axiosInstance.post(
    `${PATH}/v1/common_api/admin/printer/test`,
    { branchId }
  );
  return response.data;
};

export const sendOutreachEmail = async (to: string, subject: string, body: string): Promise<void> => {
  await axiosInstance.post(`${PATH}/v1/common_api/admin/send-outreach-email`, { to, subject, body });
};

export const deleteCompanyBranch = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/common_api/company_branch/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const changePin = async (data: { currentPin: string; newPin: string }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/change_pin`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createCompanyBranch = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/company_branches`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getServicesOrders = async (branchId: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/service_orders/${branchId}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};
export const getServicesCalendarOrders = async (
  branchId: any,
  startTime: string,
  endTime: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/service_orders_calendar/${branchId}/${startTime}/${endTime}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getBranchInfo = async ({ id }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/company_branch/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getResourcesCategories = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/admin/resource_categories`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createResourcesCategories = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/services_api/resource_categories`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getServiceCategory = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/admin/service_category/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getResourceCategory = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/admin/resource_category/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getServices = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/admin/services/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getServiceOrder = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/service_order/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getResources = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/resources/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getResource = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/resource/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createServicesCategories = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/services_api/service_categories`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateServiceCategory = async ({ id, body }: { id: number; body: any }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/services_api/service_category/${id}`,
      body
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deleteServiceCategory = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/services_api/service_category/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createServices = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/services_api/services`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateService = async ({ id, body }: { id: number; body: any }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/services_api/service/${id}`,
      body
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deleteService = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/services_api/service/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getService = async ({ id }: { id: string }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/service/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const uploadServiceImage = async (serviceId: number, file: any): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/services_api/service/${serviceId}/image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createResource = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/services_api/resources`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateResourceCategory = async ({ id, body }: { id: number; body: any }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/services_api/resource_category/${id}`,
      body
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deleteResourceCategory = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/services_api/resource_category/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getResourcesCategoriesWithResources = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/admin/resource_categories`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateResource = async ({ id, body }: { id: number; body: any }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/services_api/resource/${id}`,
      body
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deleteResource = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/services_api/resource/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createAvailabilityException = async (data: {
  from: string;
  till: string;
  resourceIds: number[];
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/services_api/availability-exception`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deleteAvailabilityException = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/services_api/availability-exception/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getDeliveryZones = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/delivery-zones/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const setDeliveryZones = async ({
  data,
}: {
  data: any;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/delivery-zone`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const sendWaitingTimeNotify = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/product/notify`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateDeliveryZone = async ({ id, data }: { id: number; data: any }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.put<any>(
      `${PATH}/v1/common_api/delivery-zone/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deleteDeliveryZone = async ({ id }: { id: any }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/common_api/delivery-zone/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};
export const getDeliveryMethods = async ({ id }: { id: any }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/delivery-methods/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getProductOrders = async ({
  id,
  status,
  filters,
}: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/product-orders/${id}/${status}`,
      filters
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateProductOrders = async ({ id, body }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/products_api/product-orders/${id}`,
      body
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getProductOrderDetails = async ({ id }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/products_api/product-order-details/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createServiceOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/services_api/service_order`,
      data
    );
    return response;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getServiceAvailability = async ({
  serviceId,
  date,
}: {
  serviceId: number;
  date: string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/service_availability/${serviceId}/${date}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getAvailableResources = async ({
  resourceCategoryId,
  serviceId,
  date,
}: {
  resourceCategoryId: number;
  serviceId: number;
  date: string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/services_api/available_resources/${resourceCategoryId}/${serviceId}/${date}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getPaymentMethods = async ({
  branchId,
}: {
  branchId: number;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/payment_methods/${branchId}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createPaymentMethod = async (body: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/payment_method`,
      body
    );
    return response;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updatePaymentMethod = async ({ id, body }: { id: number; body: any }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/common_api/payment_method/${id}`,
      body
    );
    return response;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deletePaymentMethod = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/common_api/payment_method/${id}`
    );
    return response;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

// DELIVERY METHODS (Admin)
export const getDeliveryMethodsForAdmin = async ({
  branchId,
}: {
  branchId: number;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/delivery_methods/${branchId}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createDeliveryMethodAdmin = async (body: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/delivery_method`,
      body
    );
    return response;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateDeliveryMethodAdmin = async ({ id, body }: { id: number; body: any }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/common_api/admin/delivery_method/${id}`,
      body
    );
    return response;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deleteDeliveryMethodAdmin = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/common_api/admin/delivery_method/${id}`
    );
    return response;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateServiceOrders = async ({ id, body }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/services_api/service_order/${id}`,
      body
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const changeServiceOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/services_api/admin/change-services-order`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const makeRefund = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/refund`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getProductCategories = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/products_api/admin/categories`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getProductExtensionCategories = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/products_api/admin/extension/categories`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const changeCategoriesOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/change-categories-order`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const changeServiceCategoriesOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/services_api/admin/change-service-category-order`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createProductCategory = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/category`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createProductExtensionCategory = async (
  data: any
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/extension/category`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createProduct = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/product`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createProductVariant = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/product/variant`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateProduct = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/products_api/admin/product/${data.id}`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const changeProductCategory = async (data: { productId: number; newCategoryId: number }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/products_api/admin/product/${data.productId}/change-category`,
      { newCategoryId: data.newCategoryId }
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const duplicateProduct = async (productId: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/product/${productId}/duplicate`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createProductExtension = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/product-extension`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const addVarint = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/product/variant/${data.extensionCategoryId}`,
      {
        name: data.name,
        priceExtension: data.priceExtension,
      }
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};
export const removeVarint = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/products_api/admin/product/variant/${data.name}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const editProductExtensionWithVariants = async (
  data: any
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/products_api/admin/product/extensions/variant/${data.id}`,
      {
        productExtension: data.productExtension,
        variants: data.variants,
      }
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const editProductExtensionCategory = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/products_api/admin/product/extensions/category/${data.id}`,
      data.patchData
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const removeProductExtensionCategory = async (
  data: any
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/products_api/admin/product/extensions/category/${data.id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const removeProductExtensionWithVariants = async (
  data: any
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/products_api/admin/product/extensions/variant/${data.id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getProducts = async ({ categoryId, branchId }: any): Promise<any> => {
  try {
    const url = branchId
      ? `${PATH}/v1/products_api/admin/products/${categoryId}?branchId=${branchId}`
      : `${PATH}/v1/products_api/admin/products/${categoryId}`;
    const response: AxiosResponse<any> = await axiosInstance.get<any>(url);
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getExtensions = async ({ categoryId }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/products_api/admin/product/extensions/${categoryId}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getProduct = async ({ productID }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/products_api/admin/product/${productID}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const changeProductOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/change-products-order`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const changeProductExtensionOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/change-products-extension-order`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const changeExtensionCategoriesOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/admin/change-extension-categories-order`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const removeImage = async ({
  productID,
  imageID,
}: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/products_api/admin/product/${productID}/${imageID}/image`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const removeProduct = async ({ productID }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/products_api/admin/product/${productID}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const removeCategory = async ({
  productCategoryId,
}: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/products_api/admin/category/${productCategoryId}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getCategory = async ({ productCategoryId }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/products_api/admin/category/${productCategoryId}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateCategory = async ({
  productCategoryId,
  data,
}: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/products_api/admin/category/${productCategoryId}`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createPayments = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/payments`,
      {
        ...data,
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getPaymentsMethod = async ({ id }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/payment_methods/${id}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const createSystemProductOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/system-product`,
      {
        ...data,
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const prepareSmsMessage = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/prepare-sms`,
      {
        ...data,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendSmsMessage = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/send-sms`,
      {
        ...data,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendEmailMessage = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/send-emails`,
      {
        ...data,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getClients = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/clients`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addClient = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/clients`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteClient = async (id: string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/common_api/clients/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCompany = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/company`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ONLINE ORDERING AGREEMENT
export const checkOnlineOrderingStatus = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/online-ordering/check`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const editCompany = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/common_api/admin/company/${data.id}`,
      data.data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};


export const fetchGusData = async (nip: string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/company/gus?nip=${nip}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const checkSlugAvailability = async (slug: string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/company/check-slug`,
      { params: { slug } }
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getCurrentUser = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/current_user`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateCurrentUser = async (data: { firstName: string; lastName: string }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/common_api/admin/current_user`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const completeTour = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/complete_tour`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const signAgreement = async (data: {
  agreementId: number;
  checkboxes: Record<string, boolean>;
  smsCode?: string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/online-ordering/agreement/sign`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const changePassword = async (data: { currentPassword: string; newPassword: string }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/change_password`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteAccount = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/common_api/admin/delete_account`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getAdminCount = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/admin_count`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const prepareAgreement = async (data: {
  firstName: string;
  lastName: string;
  companyName: string;
  companyAddress: string;
  nip: string;
  regon?: string;
  phoneNumber: string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/online-ordering/agreement/prepare`,

      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

// Product Categories with Products for Branch (Admin)
export const getProductCategoriesForBranch = async ({
  id,
}: {
  id: number;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/products_api/admin/categories-with-products/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

// Create Product Order
export const createProductOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/products_api/product_order`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

// COUPONS
export const createCoupon = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/coupons`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getCoupons = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/coupons`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getCouponById = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/coupons/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateCoupon = async (id: number, data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/common_api/admin/coupons/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deleteCoupon = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/common_api/admin/coupons/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const validateCoupon = async (data: {
  code: string;
  companyId?: number;
  companyBranchId?: number;
  cartTotal: number;
  productIds?: number[];
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/coupons/validate`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getProductsForAdmin = async (): Promise<any> => {
  try {
    // Fetch all categories with products
    const categories = await getProductCategories();

    // Flatten all products from all categories
    const allProducts: any[] = [];
    for (const category of categories) {
      if (category.productBases && Array.isArray(category.productBases)) {
        allProducts.push(...category.productBases);
      }
    }

    return allProducts;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createProductExtensionVariant = async (data: {
  extensionCategoryId: number;
  name: string;
  priceExtension?: number;
}): Promise<any> => {
  const response = await axiosInstance.post(
    `${PATH}/v1/products_api/admin/product/variant/${data.extensionCategoryId}`,
    {
      name: data.name,
      priceExtension: data.priceExtension || 0,
    }
  );
  return response.data;
};

export const deleteProductExtensionVariant = async (data: {
  variantName: string;
}): Promise<any> => {
  const response = await axiosInstance.delete(
    `${PATH}/v1/products_api/admin/product/variant/${encodeURIComponent(data.variantName)}`
  );
  return response.data;
};

export const renameProductExtensionVariant = async (data: {
  oldName: string;
  newName: string;
  categoryId: number;
}): Promise<any> => {
  const response = await axiosInstance.patch(
    `${PATH}/v1/products_api/admin/product/variant/${encodeURIComponent(data.oldName)}`,
    { name: data.newName, categoryId: data.categoryId }
  );
  return response.data;
};

// STATISTICS
export const getStats = async ({
  branchId,
  dateFrom,
  dateTo,
}: {
  branchId?: number;
  dateFrom?: string;
  dateTo?: string;
}): Promise<any> => {
  try {
    const params = new URLSearchParams();
    if (branchId) params.append("branchId", String(branchId));
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    const response: any = await axiosInstance.get(
      `${PATH}/v1/common_api/admin/stats?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

// COUPON CAMPAIGNS
export const previewCouponCampaign = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/coupon-campaigns/preview`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const executeCouponCampaign = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/coupon-campaigns/execute`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};


export const sendManualCoupon = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/marketing/send-coupon`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

// LOYALTY
export const getLoyaltyCards = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/loyalty/cards`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const createLoyaltyCard = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/loyalty/cards`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const updateLoyaltyCard = async (id: number, data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${PATH}/v1/common_api/admin/loyalty/cards/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const deleteLoyaltyCard = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/common_api/admin/loyalty/cards/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getLoyaltyCardStats = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/loyalty/cards/${id}/stats`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const smartStamp = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/loyalty/stamp/smart`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const addManualStamp = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/loyalty/stamps/manual`,
      data
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const removeStamp = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      `${PATH}/v1/common_api/admin/loyalty/stamps/${id}`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getLoyaltyClients = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/loyalty/clients`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const getLoyaltyDashboard = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/loyalty/dashboard`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

// BILLING / INVOICES
export const getInvoices = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `${PATH}/v1/common_api/admin/invoices`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const initiateInvoicePayment = async (invoiceId: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      `${PATH}/v1/common_api/admin/invoices/${invoiceId}/pay`
    );
    return response.data;
  } catch (error) {
    if ((error as any)?.response?.status == 401) {
      autoLogout();
    }
    throw error;
  }
};

export const reactQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
      // Do not retry on 401 — session expired, autoLogout handles redirect
      retry: (failureCount, error: any) => {
        if (error?.response?.status === 401) return false;
        return failureCount < 2;
      },
    },
  },
});
